import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.prefetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#teardownQuickAddPrefetch();
  }

  /**
   * Opens the quick add modal from controls rendered inside the product card content.
   * @param {Event} event
   */
  openQuickAdd(event) {
    event.preventDefault();
    event.stopPropagation();

    this.refs.quickAdd?.handleClick(event);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Prefetch product markup for quick-add on desktop hover/focus only.
   * Never auto-prefetch on scroll/visibility — that floods PLPs with network + DOM parse work.
   */
  #handleQuickAdd = () => {
    this.#teardownQuickAddPrefetch();

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  #teardownQuickAddPrefetch() {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.prefetchProductPage(this.productPageUrl, { force: true });

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedOption = this.variantPicker.selectedOption;
    const gallerySrcs = this.#gallerySrcsFrom(selectedOption);

    if (gallerySrcs.length) {
      this.#applyConnectedProductGallery(gallerySrcs, selectedOption.dataset.silhouetteSrc);
      return;
    }

    const patternSrc = selectedOption.dataset.colorPatternSrc;

    if (patternSrc) {
      this.#applyColorPatternImage(patternSrc);
      return;
    }

    this.#clearColorPatternImage();

    const selectedImageId = selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * @param {HTMLElement | null | undefined} input
   * @returns {string[]}
   */
  #gallerySrcsFrom(input) {
    if (!(input instanceof HTMLElement)) return [];

    const fromGallery = (input.dataset.gallerySrcs || '')
      .split('|')
      .map((src) => src.trim())
      .filter(Boolean);

    if (fromGallery.length) return fromGallery;

    const patternSrc = input.dataset.colorPatternSrc;
    return patternSrc ? [patternSrc] : [];
  }

  /**
   * Replaces card gallery images with the connected color product media.
   * @param {string[]} urls
   * @param {string} [silhouetteSrc]
   */
  #applyConnectedProductGallery(urls, silhouetteSrc) {
    const { slideshow } = this.refs;
    this.#previousSlideIndex = null;

    if (!urls.length) return;

    const silhouetteImage = this.querySelector('.card-gallery__silhouette-image');
    if (silhouetteImage instanceof HTMLImageElement && silhouetteSrc) {
      this.#setCardImage(silhouetteImage, silhouetteSrc);
    }

    if (!slideshow) {
      this.#applyColorPatternImage(urls[0]);
      return;
    }

    const slideshowSlides = [...slideshow.querySelectorAll('slideshow-slide')];
    const templateSlide = slideshowSlides[0];

    if (!templateSlide) {
      this.#applyColorPatternImage(urls[0]);
      return;
    }

    const parent = templateSlide.parentElement;
    const maxSlides = Math.min(urls.length, 5);

    while (slideshowSlides.length < maxSlides && parent) {
      const clone = /** @type {HTMLElement} */ (templateSlide.cloneNode(true));
      clone.removeAttribute('hidden');
      clone.setAttribute('slide-id', `connected-media-${slideshowSlides.length}`);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('variant-image');
      parent.appendChild(clone);
      slideshowSlides.push(clone);
    }

    slideshow.updatedCallback?.();

    for (let index = 0; index < slideshowSlides.length; index++) {
      const slide = slideshowSlides[index];
      const image = slide.querySelector('.product-media__image, img');

      if (index < maxSlides) {
        slide.removeAttribute('hidden');
        slide.removeAttribute('variant-image');
        this.#setCardImage(image, urls[index]);
      } else {
        slide.setAttribute('hidden', '');
      }
    }

    slideshow.select(0, undefined, { animate: false });
    this.#preloadNextPreviewImage();
  }

  /**
   * @param {Element | null} image
   * @param {string} src
   */
  #setCardImage(image, src) {
    if (!(image instanceof HTMLImageElement) || !src) return;

    image.src = src;
    image.removeAttribute('srcset');
    image.removeAttribute('data-color-pattern-active');
    delete image.dataset.colorPatternOriginalSrc;
    delete image.dataset.colorPatternOriginalSrcset;
    delete image.dataset.colorPatternOriginalSizes;
    image.removeAttribute('loading');
  }

  /**
   * Applies a color-pattern image URL to the main card gallery image.
   * @param {string} src
   */
  #applyColorPatternImage(src) {
    const { slideshow } = this.refs;

    if (slideshow) {
      slideshow.select(0, undefined, { animate: false });
    }

    const mainImage =
      slideshow?.slides?.[0]?.querySelector('img') ||
      slideshow?.initialSlide?.querySelector('img') ||
      this.querySelector('.card-gallery img');

    if (!(mainImage instanceof HTMLImageElement) || !src) return;

    if (!mainImage.dataset.colorPatternOriginalSrc) {
      mainImage.dataset.colorPatternOriginalSrc = mainImage.currentSrc || mainImage.src;
      mainImage.dataset.colorPatternOriginalSrcset = mainImage.getAttribute('srcset') || '';
      mainImage.dataset.colorPatternOriginalSizes = mainImage.getAttribute('sizes') || '';
    }

    mainImage.src = src;
    mainImage.removeAttribute('srcset');
    mainImage.setAttribute('data-color-pattern-active', 'true');
  }

  /**
   * Restores the main card image after a color-pattern override.
   */
  #clearColorPatternImage() {
    const images = this.querySelectorAll('.card-gallery img[data-color-pattern-active], .card-gallery img[data-color-pattern-original-src]');

    for (const image of images) {
      if (!(image instanceof HTMLImageElement)) continue;

      const originalSrc = image.dataset.colorPatternOriginalSrc;
      if (originalSrc) {
        image.src = originalSrc;
      }

      const originalSrcset = image.dataset.colorPatternOriginalSrcset;
      if (originalSrcset) {
        image.setAttribute('srcset', originalSrcset);
      } else {
        image.removeAttribute('srcset');
      }

      const originalSizes = image.dataset.colorPatternOriginalSizes;
      if (originalSizes) {
        image.setAttribute('sizes', originalSizes);
      }

      image.removeAttribute('data-color-pattern-active');
      delete image.dataset.colorPatternOriginalSrc;
      delete image.dataset.colorPatternOriginalSrcset;
      delete image.dataset.colorPatternOriginalSizes;
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return (
      this.querySelector('.product-card__inline-swatches swatches-variant-picker-component') ||
      this.querySelector('swatches-variant-picker-component')
    );
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Applies a connected-color product's gallery (and silhouette) from a swatch input.
   * @param {HTMLElement} input
   */
  applyConnectedColorMedia(input) {
    const gallerySrcs = this.#gallerySrcsFrom(input);

    if (gallerySrcs.length) {
      this.#applyConnectedProductGallery(gallerySrcs, input.dataset.silhouetteSrc);
      return;
    }

    const patternSrc = input.dataset.colorPatternSrc;
    if (patternSrc) {
      this.#applyColorPatternImage(patternSrc);
    }
  }

  /**
   * Applies a connected-color product image URL to the card gallery.
   * @param {string} src
   */
  applyConnectedColorImage(src) {
    this.#applyConnectedProductGallery([src]);
  }

  /**
   * Updates all product-card links toward a connected color product.
   * @param {string} href
   */
  updateConnectedProductLinks(href) {
    if (!href) return;

    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

    if (productCardLink instanceof HTMLAnchorElement) {
      productCardLink.href = href;
    }
    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = href;
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = href;
    }
  }

  /**
   * Previews a color-pattern image from the hovered swatch input.
   * @param {Event} event
   */
  previewColorPattern(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const input = target.closest('label')?.querySelector('input[data-color-pattern-src]');
    const patternSrc = input instanceof HTMLInputElement ? input.dataset.colorPatternSrc : null;

    if (!patternSrc) return;

    this.resetVariant.cancel();
    this.#applyColorPatternImage(patternSrc);
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const patternInput = this.querySelector(`input[data-option-media-id="${id}"][data-color-pattern-src]`);

    const patternSrc =
      patternInput instanceof HTMLInputElement ? patternInput.dataset.colorPatternSrc : null;

    if (patternSrc) {
      this.#applyColorPatternImage(patternSrc);
      return;
    }

    this.#clearColorPatternImage();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const selectedOption = this.variantPicker.selectedOption;
      const gallerySrcs = this.#gallerySrcsFrom(selectedOption);

      if (gallerySrcs.length) {
        this.#applyConnectedProductGallery(gallerySrcs, selectedOption.dataset.silhouetteSrc);
        return;
      }

      const patternSrc = selectedOption.dataset.colorPatternSrc;

      if (patternSrc) {
        this.#applyColorPatternImage(patternSrc);
        return;
      }

      this.#clearColorPatternImage();

      const id = selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    this.#clearColorPatternImage();

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"], .wishlist-hero-product-control, .wishlist-hero-custom-button'
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;
    const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl;
    const modalContent = this.closest('#quick-add-modal-content');

    // Resolve product card: on the card itself, or via the open quick-add when swatches are cloned into the modal
    let parentCard = this.parentProductCard;
    if (!(parentCard instanceof ProductCard) && modalContent) {
      const dialog = document.getElementById('quick-add-dialog');
      const activeQuickAdd =
        dialog instanceof HTMLElement && 'getActiveQuickAdd' in dialog
          ? /** @type {{ getActiveQuickAdd?: () => Element | null }} */ (dialog).getActiveQuickAdd?.()
          : null;
      parentCard =
        /** @type {ProductCard | null} */ (activeQuickAdd?.closest('product-card')) ||
        /** @type {ProductCard | null} */ (document.querySelector('quick-add-component[stay-visible]')?.closest('product-card'));
    }

    // Connected color products: update image + card links (card or quick-buy modal).
    if (isSwatchInput && connectedProductUrl && parentCard instanceof ProductCard) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      parentCard.applyConnectedColorMedia(clickedSwatch);

      const targetUrl = new URL(connectedProductUrl, window.location.origin);
      if (clickedSwatch.dataset.variantId) {
        targetUrl.searchParams.set('variant', clickedSwatch.dataset.variantId);
      }

      parentCard.updateConnectedProductLinks(targetUrl.href);
      this.dataset.productUrl = connectedProductUrl;

      const quickAdd =
        /** @type {{ switchConnectedColorInModal?: (input: HTMLElement) => void, prefetchProductPage?: Function } | null} */ (
          parentCard.querySelector('quick-add-component')
        );

      if (modalContent && quickAdd && typeof quickAdd.switchConnectedColorInModal === 'function') {
        quickAdd.switchConnectedColorInModal(clickedSwatch);
      } else {
        quickAdd?.prefetchProductPage?.(
          /** @type {{ productPageUrl?: string }} */ (parentCard).productPageUrl || targetUrl.href,
          { force: true }
        );
      }
      return;
    }

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // Same-product color in quick-buy modal: update gallery without adding to cart
    if (isSwatchInput && modalContent) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);
      const dialog = document.getElementById('quick-add-dialog');
      if (dialog instanceof HTMLElement && 'applyModalGalleryFromColorInput' in dialog) {
        /** @type {{ applyModalGalleryFromColorInput?: (input: HTMLElement) => void }} */ (
          dialog
        ).applyModalGalleryFromColorInput?.(clickedSwatch);
      }
      const variantId = clickedSwatch.dataset.variantId || firstAvailableVariantId;
      const quickAdd = document.querySelector('quick-add-component[stay-visible]');
      if (variantId && quickAdd && 'selectColor' in quickAdd) {
        /** @type {{ selectColor?: (id: string, event: Event) => void }} */ (quickAdd).selectColor?.(
          variantId,
          event
        );
      }
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
